import { cn } from "@/utils";

// Volvamed's hand-drawn scenes: a soft blue disc, a sun behind a cloud, a
// sprig of leaves and one object in Volvamed Blue with a yellow accent. They
// illustrate empty states and serve as editorial covers
// (scripts/build-editorial-covers.mjs). Purely decorative, hidden from
// assistive technologies: the surrounding text carries the message.

function Scene({ className, children }) {
  return (
    <svg
      viewBox="0 0 240 180"
      className={cn("h-auto w-40 shrink-0", className)}
      aria-hidden="true"
      focusable="false"
    >
      <circle cx="120" cy="98" r="74" fill="#E7F2FF" />
      <circle cx="178" cy="50" r="18" fill="#F5CD47" />
      <path
        d="M150 66c0-8 6-14 14-14 6 0 11 3 13 8 2-1 4-2 7-2 7 0 12 5 12 12H150z"
        fill="#ffffff"
      />
      {children}
    </svg>
  );
}

function Leaves() {
  return (
    <>
      <path
        d="M58 150c2-18 4-32 12-48"
        stroke="#7FAF8F"
        strokeWidth="3"
        strokeLinecap="round"
        fill="none"
      />
      <path d="M66 112c-10-2-16-10-15-18 9 1 15 8 15 18z" fill="#9CC5A8" />
      <path d="M68 126c10-3 15-11 13-19-9 2-14 9-13 19z" fill="#9CC5A8" />
      <path d="M62 140c-9-1-15-8-15-15 8 0 14 6 15 15z" fill="#9CC5A8" />
    </>
  );
}

// A mug of tea: a pause, nothing urgent.
export function CalmIllustration({ className }) {
  return (
    <Scene className={className}>
      <Leaves />
      <ellipse cx="116" cy="150" rx="44" ry="7" fill="#BCD0E6" />
      <path
        d="M144 110h6a12 12 0 0 1 0 24h-6"
        stroke="#26558D"
        strokeWidth="7"
        fill="none"
      />
      <rect x="88" y="98" width="58" height="50" rx="14" fill="#26558D" />
      <path
        d="M117 116c-3-4-10-2-10 3 0 6 10 11 10 11s10-5 10-11c0-5-7-7-10-3z"
        fill="#F5CD47"
      />
      <g
        stroke="#26558D"
        strokeOpacity="0.35"
        strokeWidth="4"
        strokeLinecap="round"
        fill="none"
      >
        <path d="M104 86c-6-7 6-11 0-19" />
        <path d="M117 82c-6-8 6-12 0-21" />
        <path d="M130 86c-6-7 6-11 0-19" />
      </g>
    </Scene>
  );
}

// A small desk with a lamp and a plant: a place, a cabinet.
export function DeskIllustration({ className }) {
  return (
    <Scene className={className}>
      <ellipse cx="120" cy="152" rx="64" ry="6" fill="#BCD0E6" />
      <rect x="70" y="114" width="8" height="38" rx="4" fill="#26558D" />
      <rect x="162" y="114" width="8" height="38" rx="4" fill="#26558D" />
      <rect x="126" y="114" width="40" height="20" rx="5" fill="#1D4470" />
      <circle cx="146" cy="124" r="3" fill="#F5CD47" />
      <rect x="58" y="106" width="124" height="11" rx="5.5" fill="#26558D" />
      <ellipse cx="84" cy="105" rx="12" ry="3" fill="#1D4470" />
      <path
        d="M84 104l8-30 12-6"
        stroke="#1D4470"
        strokeWidth="4"
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="none"
      />
      <path d="M96 58l22 7-8 15-20-6z" fill="#F5CD47" />
      <rect x="100" y="97" width="24" height="9" rx="2" fill="#ffffff" />
      <path d="M140 106l4-15h16l4 15z" fill="#F5CD47" />
      <path
        d="M152 91c0-8 0-13 0-17"
        stroke="#7FAF8F"
        strokeWidth="3"
        strokeLinecap="round"
      />
      <path d="M152 86c-9-1-14-7-13-14 8 1 13 7 13 14z" fill="#9CC5A8" />
      <path d="M152 80c8-2 12-8 11-14-7 1-11 7-11 14z" fill="#9CC5A8" />
    </Scene>
  );
}

// Two people side by side under a heart: clients, relatives, professionals.
export function CustomersIllustration({ className }) {
  return (
    <Scene className={className}>
      <Leaves />
      <ellipse cx="124" cy="152" rx="56" ry="6" fill="#BCD0E6" />
      <circle cx="146" cy="94" r="14" fill="#8FB3DB" />
      <path d="M118 152c0-22 12-36 28-36s28 14 28 36z" fill="#8FB3DB" />
      <circle cx="104" cy="84" r="16" fill="#26558D" />
      <path d="M72 152c0-26 14-42 32-42s32 16 32 42z" fill="#26558D" />
      <path
        d="M126 54c-3-4-10-2-10 3 0 6 10 11 10 11s10-5 10-11c0-5-7-7-10-3z"
        fill="#F5CD47"
      />
    </Scene>
  );
}

// A heart held in open palms: care, payment received, everything settled.
export function HandCareIllustration({ className }) {
  return (
    <Scene className={className}>
      <Leaves />
      <path
        d="M120 124c-24-15-36-28-36-41 0-11 8-19 18-19 8 0 14 4 18 11 4-7 10-11 18-11 10 0 18 8 18 19 0 13-12 26-36 41z"
        fill="#F5CD47"
      />
      <path
        d="M118 154c-26-2-44-18-48-42-1-5 3-8 7-6l4 3c8 16 20 25 37 27z"
        fill="#26558D"
      />
      <path
        d="M122 154c26-2 44-18 48-42 1-5-3-8-7-6l-4 3c-8 16-20 25-37 27z"
        fill="#26558D"
      />
      <path
        d="M84 128l-6 5M94 136l-5 6M146 136l5 6M156 128l6 5"
        stroke="#E7F2FF"
        strokeWidth="2.5"
        strokeLinecap="round"
      />
      <path
        d="M168 86v10M163 91h10M78 70v8M74 74h8"
        stroke="#ffffff"
        strokeWidth="3"
        strokeLinecap="round"
      />
    </Scene>
  );
}

// A laptop showing someone on a video call: visio, online tools.
export function LaptopIllustration({ className }) {
  return (
    <Scene className={className}>
      <Leaves />
      <ellipse cx="124" cy="148" rx="62" ry="6" fill="#BCD0E6" />
      <rect x="78" y="62" width="92" height="64" rx="8" fill="#26558D" />
      <rect x="84" y="68" width="80" height="52" rx="4" fill="#ffffff" />
      <circle cx="124" cy="88" r="9" fill="#F5CD47" />
      <path d="M108 120c0-12 7-19 16-19s16 7 16 19z" fill="#F5CD47" />
      <rect x="146" y="72" width="14" height="10" rx="2" fill="#BCD0E6" />
      <path d="M64 126h120l-6 12H70z" fill="#1D4470" />
      <rect x="112" y="126" width="24" height="4" rx="2" fill="#26558D" />
    </Scene>
  );
}

// A calendar page with one day marked by a heart: appointments, openings.
export function CalendarIllustration({ className }) {
  return (
    <Scene className={className}>
      <Leaves />
      <ellipse cx="122" cy="152" rx="50" ry="6" fill="#BCD0E6" />
      <rect x="78" y="62" width="88" height="84" rx="12" fill="#ffffff" />
      <path
        d="M78 74a12 12 0 0 1 12-12h64a12 12 0 0 1 12 12v12H78z"
        fill="#26558D"
      />
      <rect x="98" y="54" width="7" height="16" rx="3.5" fill="#1D4470" />
      <rect x="139" y="54" width="7" height="16" rx="3.5" fill="#1D4470" />
      <g fill="#BCD0E6">
        <circle cx="96" cy="102" r="5" />
        <circle cx="114" cy="102" r="5" />
        <circle cx="132" cy="102" r="5" />
        <circle cx="150" cy="102" r="5" />
        <circle cx="96" cy="120" r="5" />
        <circle cx="114" cy="120" r="5" />
        <circle cx="150" cy="120" r="5" />
        <circle cx="96" cy="136" r="5" />
        <circle cx="114" cy="136" r="5" />
      </g>
      <path
        d="M132 115c-3-4-10-2-10 3 0 6 10 11 10 11s10-5 10-11c0-5-7-7-10-3z"
        fill="#F5CD47"
      />
    </Scene>
  );
}

// A magnifying glass over a leaf: a search with nothing (yet) to show.
export function SearchIllustration({ className }) {
  return (
    <Scene className={className}>
      <ellipse cx="124" cy="152" rx="46" ry="6" fill="#BCD0E6" />
      <path
        d="M134 116l26 26"
        stroke="#1D4470"
        strokeWidth="13"
        strokeLinecap="round"
      />
      <circle cx="112" cy="94" r="30" fill="#ffffff" />
      <path
        d="M104 110c2-10 6-18 14-26"
        stroke="#7FAF8F"
        strokeWidth="3"
        strokeLinecap="round"
        fill="none"
      />
      <path d="M110 98c-9-1-14-7-13-14 8 1 13 7 13 14z" fill="#9CC5A8" />
      <path d="M116 90c8-3 12-9 10-16-7 2-11 8-10 16z" fill="#9CC5A8" />
      <circle
        cx="112"
        cy="94"
        r="30"
        stroke="#26558D"
        strokeWidth="8"
        fill="none"
      />
      <path
        d="M96 78a20 20 0 0 1 10-8"
        stroke="#E7F2FF"
        strokeWidth="4"
        strokeLinecap="round"
        fill="none"
      />
    </Scene>
  );
}

// A letter with a heart slipping out of its envelope: messages, reviews.
export function MessageIllustration({ className }) {
  return (
    <Scene className={className}>
      <Leaves />
      <ellipse cx="122" cy="152" rx="54" ry="6" fill="#BCD0E6" />
      <rect x="86" y="58" width="72" height="56" rx="6" fill="#ffffff" />
      <path
        d="M122 66c-3-4-10-2-10 3 0 6 10 11 10 11s10-5 10-11c0-5-7-7-10-3z"
        fill="#F5CD47"
      />
      <rect x="74" y="84" width="96" height="62" rx="10" fill="#26558D" />
      <path
        d="M78 90l44 30 44-30"
        stroke="#ffffff"
        strokeOpacity="0.5"
        strokeWidth="4"
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="none"
      />
    </Scene>
  );
}

// Rising bars with a star: figures and activity over a period.
export function ChartIllustration({ className }) {
  return (
    <Scene className={className}>
      <Leaves />
      <ellipse cx="124" cy="150" rx="54" ry="6" fill="#BCD0E6" />
      <rect x="84" y="112" width="20" height="36" rx="6" fill="#BCD0E6" />
      <rect x="112" y="94" width="20" height="54" rx="6" fill="#8FB3DB" />
      <rect x="140" y="76" width="20" height="72" rx="6" fill="#26558D" />
      <path
        d="M150 52l3.5 7 7.5 1-5.5 5.5 1.5 7.5-7-3.5-7 3.5 1.5-7.5-5.5-5.5 7.5-1z"
        fill="#F5CD47"
      />
    </Scene>
  );
}

// A sheet with lines and a yellow seal: invoices, guides, documents.
export function DocumentIllustration({ className }) {
  return (
    <Scene className={className}>
      <Leaves />
      <ellipse cx="122" cy="154" rx="48" ry="6" fill="#BCD0E6" />
      <path
        d="M86 56h52l20 20v70a8 8 0 0 1-8 8H86a8 8 0 0 1-8-8V64a8 8 0 0 1 8-8z"
        fill="#ffffff"
      />
      <path d="M138 56v12a8 8 0 0 0 8 8h12z" fill="#BCD0E6" />
      <g fill="#BCD0E6">
        <rect x="90" y="74" width="34" height="6" rx="3" />
        <rect x="90" y="90" width="56" height="6" rx="3" />
        <rect x="90" y="104" width="56" height="6" rx="3" />
        <rect x="90" y="118" width="38" height="6" rx="3" />
      </g>
      <circle cx="146" cy="136" r="15" fill="#26558D" />
      <path
        d="M139 136l5 5 9-10"
        stroke="#F5CD47"
        strokeWidth="4"
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="none"
      />
    </Scene>
  );
}
